/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import {
  Typography,
  makeStyles,
  Theme,
  createStyles,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import LockIcon from "@material-ui/icons/Lock";
import { UserConfigDataRow } from "../../../../types/config";
import { AlertDialogData } from "../../../../types";
import { ConfirmDialog, AlertDialog } from "../../../../components";
import { UsersApi, errorToStr } from "../../../../helpers";

/** スタイル定義 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    root: {
      fontSize: 13,
    },
    typography: {
      fontSize: "0.875rem",
    },
    iconButton: {
      padding: "0.25rem",
    },
  })
);

interface Props {
  rowData: UserConfigDataRow;
  onChangeVisibility: () => void;
  onResetPassword: () => void;
}

/** ユーザーパスワード表示コンポーネント */
export function UserPasswordRenderComponent(props: Props) {
  const classes = useStyles();

  const { rowData, onChangeVisibility, onResetPassword } = props;

  // 確認ダイアログ
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState<boolean>(
    false
  );

  // 結果ダイアログ
  const [alertDialogData, setAlertDialogData] = React.useState<AlertDialogData>(
    {
      open: false,
      title: "",
      message: "",
    }
  );

  // リセットされたか？
  const [isReset, setIsReset] = React.useState<boolean>(false);

  // 仮パスワードコンポーネント取得
  const getTemporaryPasswordComponent = () => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        {rowData.password.renderVisible ? (
          <Typography component="span" className={classes.typography}>
            {rowData.password.value}
          </Typography>
        ) : (
          <Typography component="span" className={classes.typography}>
            ●●●●●●●●
          </Typography>
        )}
        {/* 表示/非表示 切替アイコン */}
        <IconButton
          aria-label="toggle password visibility"
          className={classes.iconButton}
          onClick={onChangeVisibility}
          onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
          }}
        >
          {rowData.password.renderVisible ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      </div>
    );
  };

  // 本パスワードコンポーネント取得
  const getRegularPasswordComponent = () => {
    return (
      <>
        <Tooltip
          title="ユーザーによりパスワード変更済み"
          aria-label="ユーザーによりパスワード変更済み"
          enterDelay={1000}
          arrow
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Typography component="span" className={classes.typography}>
              ●●●●●●●●
            </Typography>
            <IconButton
              aria-label="toggle password visibility"
              className={classes.iconButton}
              onClick={() => {
                setOpenConfirmDialog(true);
              }}
              onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
              }}
            >
              <LockIcon />
            </IconButton>
          </div>
        </Tooltip>
        <ConfirmDialog
          open={openConfirmDialog}
          title="パスワードリセット"
          okLabel="リセット"
          okColor="secondary"
          onOk={() => {
            // API通信
            const fetchData = async () => {
              try {
                if (rowData.id && typeof rowData.id === "number") {
                  await UsersApi.resetPassword(rowData.id);
                  // 成功
                  setIsReset(true);
                  // 確認ダイアログ閉じる
                  setOpenConfirmDialog(false);
                  // 結果ダイアログ開く
                  setAlertDialogData({
                    open: true,
                    title: "パスワードリセット",
                    message:
                      "パスワードをリセットしました。仮パスワードをユーザーに通知して下さい。",
                  });
                }
              } catch (error: any) {
                // 失敗
                // 確認ダイアログ閉じる
                setOpenConfirmDialog(false);
                // 結果ダイアログ開く
                setAlertDialogData({
                  open: true,
                  title: "エラー",
                  message: errorToStr(error),
                });
              }
            };
            fetchData();
          }}
          onCancel={() => {
            setOpenConfirmDialog(false);
          }}
        >
          <p>ユーザーによりパスワードが変更済みです。</p>
          <p>&nbsp;</p>
          <p>
            ユーザーがパスワードを忘れた場合はパスワードをリセットできます。
          </p>
          <p>パスワードをリセットしますか？</p>
        </ConfirmDialog>
        <AlertDialog
          data={alertDialogData}
          handleClose={() => {
            // ダイアログを閉じる
            setAlertDialogData({
              ...alertDialogData,
              open: false,
            });
            if (isReset) {
              // リセットされたことを通知する
              onResetPassword();
            }
          }}
        />
      </>
    );
  };

  let component;
  if (rowData.password.isTemporary) {
    // 仮パスワード（管理者が編集できる）
    component = getTemporaryPasswordComponent();
  } else {
    // 本パスワード
    component = getRegularPasswordComponent();
  }

  //--------------------
  // コンポーネント
  return component;
}
